import { useRef, type ClipboardEvent } from "react";

/**
 * KOD ALANI — tek kullanımlık kodun hane hane girildiği kutular
 *
 * ================================================================
 * NEDEN TEK ALAN DEĞİL
 * ================================================================
 * Tek bir `<input>` da işi görürdü. Kutular çizimin kararı ve
 * sebebi var: kullanıcı telefonundaki koda bakarken ekranda kaçıncı
 * hanede olduğunu SAYMADAN görüyor. Altı haneyi tek kutuda okumak,
 * araya boşluk konmadığı sürece hep bir kez daha bakmayı
 * gerektiriyor — ve kod 30 saniyede bir değişiyor.
 *
 * ================================================================
 * NEDEN `packages/ui` İÇİNDE
 * ================================================================
 * Panelde yazılmıştı ve `form-bileseni.test.ts` bekçisi haklı olarak
 * düşürdü: arayüz uygulamalarında ham `<input>` yasak, çünkü elle
 * yazılan alanlar 44px dokunma hedefini ve etiket bağını kaybediyor.
 * Bekçiyi susturmak yerine alan doğru yere taşındı — bekçinin
 * amacı da tam olarak buydu.
 *
 * ================================================================
 * ETİKET HER KUTUDA VAR
 * ================================================================
 * Altı ayrı kutu, ekran okuyucuda altı ayrı alan demek. `aria-label`
 * olmasaydı kullanıcı altı isimsiz kutu duyar ve hangisinde
 * olduğunu bilemezdi.
 */

export interface KodAlaniProps {
  /** Şu ana kadar girilen rakamlar — uzunluğu `hane`yi geçmez. */
  deger: string;
  onDegisti: (yeni: string) => void;
  /** Kaç hane. TOTP için 6. */
  hane?: number;
  devreDisi?: boolean;
  /** İlk kutuya odaklan. Ekran açılır açılmaz kod girilecekse `true`. */
  odakla?: boolean;
  /** Ekran okuyucuya "neyin kaçıncı hanesi" diye söylenecek ad. */
  ad?: string;
}

export function KodAlani({
  deger,
  onDegisti,
  hane = 6,
  devreDisi = false,
  odakla = false,
  ad = "Doğrulama kodu",
}: KodAlaniProps) {
  const kutular = useRef<Array<HTMLInputElement | null>>([]);

  function haneYaz(i: number, ham: string): void {
    const rakam = ham.replace(/\D/g, "");
    if (!rakam) {
      /*
        Silme, BU HANEDEN SONRASINI da temizliyor.
        "Yalnızca bu kutuyu boşalt" daha az iş gibi görünüyor ama
        arada delik olan bir kod bırakıyor: kullanıcı beşinci haneyi
        silip düğmeye basıyor, kutular dolu görünüyor, kod eksik.
        Soldan sağa yazılan bir alanda soldan sağa silmek beklenen
        davranış.
      */
      onDegisti(deger.slice(0, i));
      return;
    }

    // Birden cok rakam geldiyse (hizli yazim ya da otomatik doldurma)
    // hepsini siraya yerlestir.
    const yeni = (deger.slice(0, i) + rakam).slice(0, hane);
    onDegisti(yeni);
    kutular.current[Math.min(yeni.length, hane - 1)]?.focus();
  }

  function tusaBasildi(i: number, tus: string): void {
    if (tus === "Backspace" && !deger[i] && i > 0) kutular.current[i - 1]?.focus();
    if (tus === "ArrowLeft" && i > 0) kutular.current[i - 1]?.focus();
    if (tus === "ArrowRight" && i < hane - 1) kutular.current[i + 1]?.focus();
  }

  /*
    YAPIŞTIRMA HERHANGİ BİR KUTUDAN çalışıyor. Kod uygulamadan
    kopyalanıp yapıştırılıyor; önce ilk kutuya tıklamak gerekseydi
    kutular tek alandan daha zahmetli olurdu.
  */
  function yapistirildi(e: ClipboardEvent<HTMLInputElement>): void {
    const rakamlar = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, hane);
    if (!rakamlar) return;
    e.preventDefault();
    onDegisti(rakamlar);
    kutular.current[Math.min(rakamlar.length, hane - 1)]?.focus();
  }

  return (
    <div className="flex gap-[9px]">
      {Array.from({ length: hane }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            kutular.current[i] = el;
          }}
          /*
            `type=text` + `inputMode=numeric`: `type=number` olsaydı
            tarayıcı ok tuşlarıyla artan bir sayaç çizerdi ve kod bir
            sayı değil, altı ayrı karakter.
          */
          type="text"
          inputMode="numeric"
          // Tek seferlik kod otomatik doldurması YALNIZCA ilk kutuda:
          // altısına birden verilseydi tarayıcı aynı kodu her kutuya
          // yazmayı denerdi.
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={hane}
          disabled={devreDisi}
          autoFocus={odakla && i === 0}
          aria-label={`${ad} — ${i + 1}. hane`}
          value={deger[i] ?? ""}
          onChange={(e) => haneYaz(i, e.target.value)}
          onKeyDown={(e) => tusaBasildi(i, e.key)}
          onPaste={yapistirildi}
          className="rounded-alan bg-notr-0 text-notr-900 ring-notr-300 ease-cikis focus:ring-marka-600 disabled:bg-notr-50 h-[62px] w-[54px] text-center text-2xl font-semibold ring-1 transition-[box-shadow] duration-(--sure-hizli) ring-inset focus:ring-2 disabled:cursor-not-allowed"
          style={{ animation: `haneOtur var(--sure-hizli) var(--ease-yay) ${i * 55}ms backwards` }}
        />
      ))}
    </div>
  );
}
